/**
 * SuccessWare Import React Query Hooks
 * 
 * Provides React Query hooks for managing SuccessWare imports.
 * Includes queries for tracking import progress and mutations for
 * starting, pausing, resuming, and cancelling imports.
 * 
 * Key features:
 * - Real-time progress tracking with automatic polling
 * - Pausable/resumable imports that survive computer shutdown
 * - Batch processing (100 records per batch)
 * - Optimistic updates for better UX
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { SuccessWareImportService, EntityType, ImportProgress, FullImportConfig } from '@/lib/services/SuccessWareImportService';
import { ImportJob } from '@/lib/repositories/SuccessWareImportRepository';

// Create service instance
const importService = new SuccessWareImportService();

/**
 * Query Keys
 */
export const importKeys = {
  all: ['successware-imports'] as const,
  lists: () => [...importKeys.all, 'list'] as const,
  list: (company_id?: string) => [...importKeys.lists(), { company_id }] as const,
  details: () => [...importKeys.all, 'detail'] as const,
  detail: (id: string) => [...importKeys.details(), id] as const,
  progress: (id: string) => [...importKeys.all, 'progress', id] as const,
  active: (company_id?: string) => [...importKeys.all, 'active', { company_id }] as const,
  summary: (company_id: string) => [...importKeys.all, 'summary', company_id] as const,
};

/**
 * Hook to fetch import jobs for a company
 * 
 * @param company_id Company ID
 * @param options Additional query options
 * 
 * @example
 * ```tsx
 * const { data: jobs = [], isLoading } = useImportJobs('company-123');
 * 
 * const completedJobs = jobs.filter(j => j.status === 'completed');
 * ```
 */
export function useImportJobs(
  company_id: string,
  options?: UseQueryOptions<ImportJob[], Error>
) {
  return useQuery<ImportJob[], Error>({
    queryKey: importKeys.list(company_id),
    queryFn: () => importService.getCompanyImports(company_id),
    enabled: !!company_id,
    staleTime: 30 * 1000, // 30 seconds - frequently changing data
    ...options,
  });
}

/**
 * Hook to fetch a single import job
 * 
 * @param job_id Job ID
 * @param options Additional query options
 * 
 * @example
 * ```tsx
 * const { data: job } = useImportJob('job-123');
 * 
 * if (job?.status === 'completed') {
 *   console.log(`Imported ${job.records_imported} records`);
 * }
 * ```
 */
export function useImportJob(
  job_id: string | null,
  options?: UseQueryOptions<ImportJob | null, Error>
) {
  return useQuery<ImportJob | null, Error>({
    queryKey: importKeys.detail(job_id || ''),
    queryFn: async () => {
      if (!job_id) return null;
      const jobs = await importService.getCompanyImports(''); // TODO: Fix this - need job_id lookup
      return jobs.find(j => j.id === job_id) || null;
    },
    enabled: !!job_id,
    staleTime: 30 * 1000,
    ...options,
  });
}

/**
 * Hook to fetch active imports with real-time polling
 * 
 * Automatically polls every 2 seconds when there are active imports.
 * Stops polling when all imports are complete.
 * 
 * @param company_id Optional company filter
 * @param options Additional query options
 * 
 * @example
 * ```tsx
 * const { data: activeImports = [] } = useActiveImports('company-123');
 * 
 * const hasActiveImports = activeImports.length > 0;
 * ```
 */
export function useActiveImports(
  company_id?: string,
  options?: UseQueryOptions<ImportJob[], Error>
) {
  return useQuery<ImportJob[], Error>({
    queryKey: importKeys.active(company_id),
    queryFn: () => importService.getActiveImports(company_id),
    staleTime: 0, // Always fetch fresh data
    refetchInterval: (query) => {
      // Poll every 2 seconds if there are active imports
      return query.state.data && query.state.data.length > 0 ? 2000 : false;
    },
    refetchIntervalInBackground: true,
    ...options,
  });
}

/**
 * Hook to fetch import progress with real-time polling
 * 
 * Automatically polls every 2 seconds for active imports.
 * Stops polling when import is complete, failed, or cancelled.
 * 
 * @param job_id Job ID
 * @param options Additional query options
 * 
 * @example
 * ```tsx
 * const { data: progress } = useImportProgress('job-123');
 * 
 * if (progress) {
 *   console.log(`${progress.percentComplete}% complete`);
 *   console.log(`ETA: ${progress.estimatedTimeRemaining}`);
 * }
 * ```
 */
export function useImportProgress(
  job_id: string | null,
  options?: UseQueryOptions<ImportProgress | null, Error>
) {
  return useQuery<ImportProgress | null, Error>({
    queryKey: importKeys.progress(job_id || ''),
    queryFn: async () => {
      if (!job_id) return null;
      return await importService.getImportProgress(job_id);
    },
    enabled: !!job_id,
    staleTime: 0, // Always fetch fresh data
    refetchInterval: (query) => {
      // Poll every 2 seconds if import is active
      const progress = query.state.data;
      const isActive = progress?.job?.status === 'running' || progress?.job?.status === 'pending';
      return isActive ? 2000 : false;
    },
    refetchIntervalInBackground: true,
    ...options,
  });
}

/**
 * Hook to fetch import summary for a company
 * 
 * @param company_id Company ID
 * @param options Additional query options
 * 
 * @example
 * ```tsx
 * const { data: summary } = useImportSummary('company-123');
 * 
 * console.log(`Total imported: ${summary?.total_records_imported}`);
 * ```
 */
export function useImportSummary(
  company_id: string,
  options?: UseQueryOptions<{
    total_jobs: number;
    active_jobs: number;
    completed_jobs: number;
    failed_jobs: number;
    total_records_imported: number;
    total_records_failed: number;
  }, Error>
) {
  return useQuery({
    queryKey: importKeys.summary(company_id),
    queryFn: () => importService.getImportSummary(company_id),
    enabled: !!company_id,
    staleTime: 30 * 1000,
    ...options,
  });
}

/**
 * Hook to start a full import for a company
 * 
 * Creates import jobs for all entity types (or specified types).
 * Automatically invalidates relevant caches after success.
 * 
 * @example
 * ```tsx
 * const startFullImport = useStartFullImport();
 * 
 * const handleStartImport = async () => {
 *   try {
 *     const jobs = await startFullImport.mutateAsync({
 *       company_id: 'company-123',
 *       batch_size: 100
 *     });
 *     console.log(`Created ${jobs.length} import jobs`);
 *   } catch (error) {
 *     console.error('Failed to start import:', error);
 *   }
 * };
 * ```
 */
export function useStartFullImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (config: FullImportConfig) => 
      importService.startFullImport(config),
    
    onSuccess: (jobs, variables) => {
      // Invalidate all import-related queries for this company
      queryClient.invalidateQueries({ 
        queryKey: importKeys.list(variables.company_id) 
      });
      queryClient.invalidateQueries({ 
        queryKey: importKeys.active(variables.company_id) 
      });
      queryClient.invalidateQueries({ 
        queryKey: importKeys.summary(variables.company_id) 
      });
    },
  });
}

/**
 * Hook to start a single entity import
 * 
 * Creates an import job for a specific entity type.
 * Automatically invalidates relevant caches after success.
 * 
 * @example
 * ```tsx
 * const startEntityImport = useStartEntityImport();
 * 
 * await startEntityImport.mutateAsync({
 *   company_id: 'company-123',
 *   entity_type: 'customers',
 *   batch_size: 100
 * });
 * ```
 */
export function useStartEntityImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      company_id, 
      entity_type, 
      batch_size = 100 
    }: { 
      company_id: string; 
      entity_type: EntityType; 
      batch_size?: number;
    }) => importService.startEntityImport(company_id, entity_type, batch_size),
    
    onSuccess: (job, variables) => {
      // Invalidate all import-related queries for this company
      queryClient.invalidateQueries({ 
        queryKey: importKeys.list(variables.company_id) 
      });
      queryClient.invalidateQueries({ 
        queryKey: importKeys.active(variables.company_id) 
      });
      queryClient.invalidateQueries({ 
        queryKey: importKeys.summary(variables.company_id) 
      });
    },
  });
}

/**
 * Hook to pause a running import
 * 
 * Saves current state to database so it can be resumed later,
 * even after computer shutdown.
 * 
 * Provides optimistic updates for immediate UI feedback.
 * 
 * @example
 * ```tsx
 * const pauseImport = usePauseImport();
 * 
 * await pauseImport.mutateAsync('job-123');
 * ```
 */
export function usePauseImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (job_id: string) => importService.pauseImport(job_id),
    
    // Optimistic update
    onMutate: async (job_id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: importKeys.progress(job_id) });

      // Snapshot previous value
      const previousProgress = queryClient.getQueryData<ImportProgress>(
        importKeys.progress(job_id)
      );

      // Optimistically update status
      if (previousProgress) {
        queryClient.setQueryData<ImportProgress>(
          importKeys.progress(job_id),
          {
            ...previousProgress,
            job: {
              ...previousProgress.job,
              status: 'paused',
              paused_at: new Date().toISOString(),
            },
          }
        );
      }

      return { previousProgress };
    },

    // Rollback on error
    onError: (_err, job_id, context) => {
      if (context?.previousProgress) {
        queryClient.setQueryData(
          importKeys.progress(job_id),
          context.previousProgress
        );
      }
    },

    // Refetch after success or error
    onSettled: (_data, _error, job_id) => {
      queryClient.invalidateQueries({ queryKey: importKeys.progress(job_id) });
      queryClient.invalidateQueries({ queryKey: importKeys.active() });
    },
  });
}

/**
 * Hook to resume a paused import
 * 
 * Loads saved state from database and continues from where it left off.
 * Works even after computer shutdown.
 * 
 * Provides optimistic updates for immediate UI feedback.
 * 
 * @example
 * ```tsx
 * const resumeImport = useResumeImport();
 * 
 * await resumeImport.mutateAsync('job-123');
 * ```
 */
export function useResumeImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (job_id: string) => importService.resumeImport(job_id),
    
    // Optimistic update
    onMutate: async (job_id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: importKeys.progress(job_id) });

      // Snapshot previous value
      const previousProgress = queryClient.getQueryData<ImportProgress>(
        importKeys.progress(job_id)
      );

      // Optimistically update status
      if (previousProgress) {
        queryClient.setQueryData<ImportProgress>(
          importKeys.progress(job_id),
          {
            ...previousProgress,
            job: {
              ...previousProgress.job,
              status: 'running',
              paused_at: null,
            },
          }
        );
      }

      return { previousProgress };
    },

    // Rollback on error
    onError: (_err, job_id, context) => {
      if (context?.previousProgress) {
        queryClient.setQueryData(
          importKeys.progress(job_id),
          context.previousProgress
        );
      }
    },

    // Refetch after success or error
    onSettled: (_data, _error, job_id) => {
      queryClient.invalidateQueries({ queryKey: importKeys.progress(job_id) });
      queryClient.invalidateQueries({ queryKey: importKeys.active() });
    },
  });
}

/**
 * Hook to cancel an import
 * 
 * Cancels the import and marks it as cancelled in the database.
 * 
 * Provides optimistic updates for immediate UI feedback.
 * 
 * @example
 * ```tsx
 * const cancelImport = useCancelImport();
 * 
 * await cancelImport.mutateAsync('job-123');
 * ```
 */
export function useCancelImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (job_id: string) => importService.cancelImport(job_id),
    
    // Optimistic update
    onMutate: async (job_id) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: importKeys.progress(job_id) });

      // Snapshot previous value
      const previousProgress = queryClient.getQueryData<ImportProgress>(
        importKeys.progress(job_id)
      );

      // Optimistically update status
      if (previousProgress) {
        queryClient.setQueryData<ImportProgress>(
          importKeys.progress(job_id),
          {
            ...previousProgress,
            job: {
              ...previousProgress.job,
              status: 'cancelled',
              completed_at: new Date().toISOString(),
            },
          }
        );
      }

      return { previousProgress };
    },

    // Rollback on error
    onError: (_err, job_id, context) => {
      if (context?.previousProgress) {
        queryClient.setQueryData(
          importKeys.progress(job_id),
          context.previousProgress
        );
      }
    },

    // Refetch after success or error
    onSettled: (_data, _error, job_id) => {
      queryClient.invalidateQueries({ queryKey: importKeys.progress(job_id) });
      queryClient.invalidateQueries({ queryKey: importKeys.active() });
    },
  });
}

/**
 * Hook to check if a company has active imports
 * 
 * Returns boolean indicating if any imports are currently running or pending.
 * Uses the active imports query data without making additional requests.
 * 
 * @param company_id Company ID
 * @returns Boolean indicating if there are active imports
 * 
 * @example
 * ```tsx
 * const hasActiveImports = useHasActiveImports('company-123');
 * 
 * if (hasActiveImports) {
 *   console.log('Import in progress...');
 * }
 * ```
 */
export function useHasActiveImports(company_id?: string): boolean {
  const { data: activeImports = [] } = useActiveImports(company_id);
  return activeImports.length > 0;
}
